import {ArcElement, Chart, Legend, PieController, Tooltip} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {min, reduce, round, trim} from 'lodash';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';

import ChartConstants from 'components/Chart/Constants';
import ChartLegend from 'components/Chart/Legend';
import {DEFAULT_FONT_FAMILY, getBreakpointName, getChartFontSize} from 'utils/browserUtils';


const propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    color: PropTypes.string,
  })),
  labels: PropTypes.bool,
  /**
   * Custom chart.js `datalabels` config props.
   */
  labelConfig: PropTypes.object,
  legend: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  /**
   * Maximum width.
   * This width will override maxWidth from style.
   */
  maxWidth: PropTypes.string,
};
/**
 * Renders pie chart.
 */
export default function PieChart({title, data, labels = true, labelConfig, legend = true, className, style,
  maxWidth}) {
  const ref = useRef(null);
  // reference: https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
  const refCallback = useCallback((node) => {
    ref.current = node;
  }, []);
  const [chart, setChart] = useState(null);

  // must set position: relative to get responsive behavior
  const finalStyle = {
    position: 'relative',
    ...style,
  };
  if (maxWidth) {
    finalStyle.maxWidth = maxWidth;
  }

  useEffect(() => {
    if (!data || data.length === 0) {
      return;
    }
    if (!ref.current) {
      return;
    }

    let dataLabelConfig;
    if (labels) {
      if (labelConfig) {
        dataLabelConfig = labelConfig;
      } else {
        dataLabelConfig = createDataLabelConfig(reduce(data, (total, d) => total + d.value, 0));
      }
    }

    const labelList = data.map((d) => d.label);
    const valueList = data.map((d) => d.value);
    const backgroundColor = data.map((d, i) => d.color || ChartConstants.colors[min([i, data.length - 1])]);
    if (!chart) {
      const datasets = [{
        data: valueList,
        backgroundColor,
      }];
      const loadChart = async () => {
        // only pull what we need for tree shaking: https://www.chartjs.org/docs/latest/getting-started/integration.html
        Chart.register(ArcElement, PieController, Legend, Tooltip);
        setChart(new Chart(ref.current.getContext('2d'), {
          type: 'pie',
          data: {
            labels: labelList,
            datasets,
          },
          ...(labels ? {plugins: [ChartDataLabels]} : {}),
          options: {
            responsive: true,
            maintainAspectRatio: false,
            ...(labels ? createLayout() : {}),
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: !labels,
              },
              ...dataLabelConfig,
            },
          },
        }));
      };
      loadChart();
    } else {
      chart.data.labels = labelList;
      chart.data.datasets[0].values = valueList;
      chart.update();
    }
  }, [title, data, labels]);

  return (
    <div>
      {title ? <div className="text-center">{title}</div> : null}
      <div className={className} style={finalStyle}>
        <canvas ref={refCallback} aria-label={title || 'Pie Chart'} role="img" id={title} />
      </div>
      {legend && (<ChartLegend data={data} />)}
    </div>
  );
}

PieChart.propTypes = propTypes;

export function createDataLabelConfig(total) {
  return {
    datalabels: {
      color: 'black',
      anchor: (context) => {
        const val = context.dataset.data[context.dataIndex];
        const percentVal = round((val / total) * 100, 1);

        if (percentVal < 15) {
          return 'end';
        } else if (percentVal < 100) {
          return 'center';
        } else {
          return 'start';
        }
      },
      align: (context) => {
        const val = context.dataset.data[context.dataIndex];
        const percentVal = round((val / total) * 100, 1);

        if (percentVal < 15) {
          return 'start';
        } else if (percentVal < 100) {
          return 'center';
        } else {
          return 'start';
        }
      },
      offset: (context) => {
        const val = context.dataset.data[context.dataIndex];
        const percentVal = round((val / total) * 100, 1);

        if (percentVal < 15) {
          return '10';
        } else if (percentVal < 100) {
          return '0';
        } else {
          return '0';
        }
      },
      display: 'auto',
      borderWidth: 0.1,
      borderColor: 'black',
      borderRadius: () => {
        switch (getBreakpointName()) {
          case 'xs':
            return 1;
          case 'sm':
            return 2;
          case 'md':
            return 3;
          default:
            return 4;
        }
      },
      backgroundColor: 'white',
      padding: () => {
        switch (getBreakpointName()) {
          case 'xs':
            return {top: 3, bottom: 3, left: 2, right: 2};
          case 'sm':
            return {top: 3, bottom: 3, left: 2, right: 2};
          case 'md':
            return {top: 5, bottom: 5, left: 2, right: 2};
          default:
            return {top: 5, bottom: 5, left: 5, right: 5};
        }
      },
      formatter: (value, context) => {
        const label = trim(context.chart.data.labels[context.dataIndex]);
        let formattedLabel = label;
        const maxWidth = 10;
        if (label.length > maxWidth) {
          const words = label.split(' ');
          formattedLabel = '';

          let curWidth = 0;
          for (let i = 0; i < words.length; i += 1) {
            curWidth += words[i].length;
            if (formattedLabel.length > 0 && curWidth > maxWidth) {
              formattedLabel += '\n';
              curWidth = words[i].length;
            } else if (formattedLabel.length > 0) {
              formattedLabel += ' ';
            }
            formattedLabel += words[i];
          }
        }
        return formattedLabel + '\n' + round((value / total) * 100, 1) + '%';
      },
      textAlign: 'center',
      opacity: 0.9,
      font: () => ({
        family: DEFAULT_FONT_FAMILY,
        size: getChartFontSize(),
        weight: 'normal',
      }),
    },
  };
}

function createLayout() {
  return {
    layout: () => {
      switch (getBreakpointName()) {
        case 'xs':
          return {padding: 10};
        case 'sm':
          return {padding: 0};
        case 'md':
          return {padding: 10};
        case 'lg':
          return {padding: 0};
        default:
          return {padding: 10};
      }
    },
  };
}
